// Standalone document upload — /upload?t=<link token>
//
// Staff send a patient one link (created per patient, on the organisation's own
// domain); the patient uploads documents into their file from any device. No
// questionnaire, no patient session: its own upload session (X-Upload-Token).
// The link keeps working until it expires or staff revoke it, so the patient can
// come back with more documents. Files go through the same pipeline as the
// questionnaire's upload step (storage + PsyCloud filing).
//
// Two ways the page looks:
// - staff asked for specific documents (e.g. EKG and Blutbild): one upload area per document,
//   each with its own button and the files that arrived for it; anything else is optional;
// - a general link: the patient says what each document is (dropdown), then uploads.
//
// "Einsenden" (Paul, 2026-10-15): uploaded files wait on the page ("bereit zum Einsenden", can be
// removed) until the patient sends them; then the practice gets one PDF per document type. Every
// requested document needs a file first. What nobody sends is sent automatically after 24 h.
// One link, one sending (2026-10-19): after "Einsenden" the link is closed ("done").
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UploadCloud, FileText, CheckCircle2, Circle, AlertTriangle, Loader2, Camera, RotateCw, X, ShieldCheck, MessageSquare, Plus, Clock, Send, Trash2, Stamp } from 'lucide-react'
import { Logo } from '../components/Logo'
import { Button } from '../components/Button'
import { useTenant } from '../context/TenantContext'
import { uploadLinkService, UploadLinkSummary, getApiErrorCode, getApiErrorStatus, getApiErrorDetails } from '../services/api'
import { MAX_UPLOAD_BYTES, formatFileSize } from '../utils/fileLimits'
import { CHOOSABLE_DOCUMENT_TYPES, isDocumentType } from '../data/documentTypes'
import { cn } from '../utils/cn'
import { logger } from '../utils/logger'
import { LegalFooter } from '../components/LegalFooter'

type Phase = 'loading' | 'invalid' | 'expired' | 'revoked' | 'done' | 'ready'
type ErrorKey = 'tooLarge' | 'unsupported' | 'heic' | 'conversion' | 'imageTooLarge' | 'failed' | 'rateLimited'

interface Item {
  key: string
  name: string
  size: number
  status: 'queued' | 'uploading' | 'done' | 'error'
  progress: number
  error?: ErrorKey
  /** kept for a retry; dropped once the upload succeeded */
  file?: File
  at?: string
  /** what the document is (data/documentTypes.ts) */
  documentType?: string | null
  /** id on the server, once stored */
  serverId?: string
  /** stored, not sent yet ("Einsenden") */
  pending?: boolean
}

const ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png'
const ALLOWED_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png'])
let nextKey = 0
/** Need the practice's stamp and a physician's signature (Paul, 2026-10-27) */
const STAMP_TYPES = new Set(['ecg', 'bloodwork'])

function serverError(err: unknown): { key: ErrorKey; terminal?: Phase; limit?: boolean } {
  const code = getApiErrorCode(err)
  if (code === 'upload_link_expired') return { key: 'failed', terminal: 'expired' }
  if (code === 'upload_link_revoked') return { key: 'failed', terminal: 'revoked' }
  if (code === 'upload_link_invalid') return { key: 'failed', terminal: 'invalid' }
  if (code === 'upload_link_submitted') return { key: 'failed', terminal: 'done' }
  if (code === 'upload_limit_reached') return { key: 'failed', limit: true }
  if (code === 'file_too_large' || getApiErrorStatus(err) === 413) return { key: 'tooLarge' }
  if (code === 'unsupported_image') return { key: 'heic' }
  if (code === 'unsupported_file') return { key: 'unsupported' }
  if (code === 'image_conversion_failed') return { key: 'conversion' }
  if (code === 'image_too_large') return { key: 'imageTooLarge' }
  if (code === 'upload_rate_limited' || getApiErrorStatus(err) === 429) return { key: 'rateLimited' }
  return { key: 'failed' }
}

export function UploadDocumentsPage() {
  const { t, i18n } = useTranslation()
  // The exchange must carry X-Tenant-ID (resolved from the hostname): wait for the tenant.
  const { tenant, loading: tenantLoading } = useTenant()

  const [phase, setPhase] = useState<Phase>('loading')
  const [session, setSession] = useState<string | null>(null)
  const [link, setLink] = useState<UploadLinkSummary | null>(null)
  const [items, setItems] = useState<Item[]>([])
  const [remaining, setRemaining] = useState(0)
  // Drop target being hovered: a requested type, or 'general'
  const [dragTarget, setDragTarget] = useState<string | null>(null)
  // The documents staff asked for, and which of them still have no file
  const [requested, setRequested] = useState<string[]>([])
  const [missing, setMissing] = useState<string[]>([])
  const [requestedAt, setRequestedAt] = useState<string | null>(null)
  // What the patient says the next file is (general link, or "another document")
  const [docType, setDocType] = useState('')
  const [showExtra, setShowExtra] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitResult, setSubmitResult] = useState<{ ok: boolean; failed?: string[] } | null>(null)
  // ECG / blood work need the practice's stamp and a physician's signature; the patient confirms before sending
  const [stampConfirmed, setStampConfirmed] = useState(false)
  const [lastSubmittedAt, setLastSubmittedAt] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const cameraInput = useRef<HTMLInputElement>(null)
  // Which upload area opened the (shared) file picker
  const pickFor = useRef<string | null>(null)
  // one upload at a time, in the order the patient chose the files
  const queue = useRef<Promise<void>>(Promise.resolve())

  const linkToken = new URLSearchParams(window.location.search).get('t') || ''
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)

  useEffect(() => {
    if (tenantLoading) return
    if (!linkToken) { setPhase('invalid'); return }
    let cancelled = false
    ;(async () => {
      try {
        const { sessionToken, link: summary } = await uploadLinkService.exchange(linkToken)
        if (cancelled) return
        if (summary.locale && summary.locale !== i18n.language) i18n.changeLanguage(summary.locale)
        setSession(sessionToken)
        setLink(summary)
        setRemaining(summary.remaining)
        setRequested((summary.requestedTypes ?? []).filter(isDocumentType))
        setMissing((summary.missingTypes ?? []).filter(isDocumentType))
        setRequestedAt(summary.requestedAt ?? null)
        // newest first from the server; the lists show oldest first, new uploads appear at the bottom
        setItems([...summary.files].reverse().map(f => ({ key: `s-${f.id}`, name: f.originalName, size: f.sizeBytes, status: 'done' as const, progress: 100, at: f.createdAt, documentType: f.documentType, serverId: f.id, pending: !!f.pending })))
        setLastSubmittedAt(summary.lastSubmittedAt ?? null)
        setPhase('ready')
      } catch (err) {
        if (cancelled) return
        const code = getApiErrorCode(err)
        // Explain it in the language the link was sent in
        const linkLocale = getApiErrorDetails(err)?.locale
        if (typeof linkLocale === 'string' && linkLocale !== i18n.language) i18n.changeLanguage(linkLocale)
        setPhase(code === 'upload_link_expired' ? 'expired' : code === 'upload_link_revoked' ? 'revoked' : code === 'upload_link_submitted' ? 'done' : 'invalid')
      }
    })()
    return () => { cancelled = true }
  }, [tenantLoading, linkToken, i18n])

  const patch = (key: string, change: Partial<Item>) => setItems(prev => prev.map(i => (i.key === key ? { ...i, ...change } : i)))

  const upload = useCallback((item: Item, sess: string) => {
    queue.current = queue.current.then(async () => {
      if (!item.file) return
      patch(item.key, { status: 'uploading', progress: 0, error: undefined })
      try {
        const r = await uploadLinkService.uploadFile(sess, item.file, item.documentType ?? null, percent => patch(item.key, { progress: percent }))
        patch(item.key, { status: 'done', progress: 100, file: undefined, at: r.file.createdAt, name: r.file.originalName, documentType: r.file.documentType ?? item.documentType, serverId: r.file.id, pending: r.file.pending ?? true })
        setSubmitResult(null)
        setRemaining(r.remaining)
        if (r.missingTypes) setMissing(r.missingTypes.filter(isDocumentType))
      } catch (err) {
        const e = serverError(err)
        logger.warn('[UploadLink] upload failed:', getApiErrorCode(err) ?? getApiErrorStatus(err))
        if (getApiErrorCode(err) === 'upload_session_expired' || getApiErrorCode(err) === 'upload_session_required') {
          // The session ran out while the page was open — the link is still valid, reload it
          window.location.reload()
          return
        }
        patch(item.key, { status: 'error', error: e.key })
        if (e.limit) setRemaining(0)
        if (e.terminal) setPhase(e.terminal)
      }
    })
  }, [])

  const addFiles = (files: FileList | File[] | null, type: string | null) => {
    if (!files || !session || !type) return
    const fresh: Item[] = []
    for (const file of Array.from(files)) {
      const item: Item = { key: `c-${nextKey++}`, name: file.name, size: file.size, status: 'queued', progress: 0, file, documentType: type }
      // Refuse locally what the server would refuse anyway, before the patient waits for the transfer
      if (/\.(heic|heif)$/i.test(file.name) || /^image\/(heic|heif)$/i.test(file.type)) { item.status = 'error'; item.error = 'heic'; item.file = undefined }
      else if (!ALLOWED_TYPES.has(file.type)) { item.status = 'error'; item.error = 'unsupported'; item.file = undefined }
      else if (file.size > MAX_UPLOAD_BYTES) { item.status = 'error'; item.error = 'tooLarge'; item.file = undefined }
      fresh.push(item)
    }
    setItems(prev => [...prev, ...fresh])
    for (const item of fresh) if (item.status === 'queued') upload(item, session)
  }

  /** Opens the file picker (or the camera) for one upload area. */
  const pick = (type: string, camera = false) => {
    pickFor.current = type
    ;(camera ? cameraInput : fileInput).current?.click()
  }
  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => { addFiles(e.target.files, pickFor.current); e.target.value = '' }
  const dropProps = (target: string, type: string | null) => ({
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); if (type) setDragTarget(target) },
    onDragLeave: (e: React.DragEvent) => { e.preventDefault(); setDragTarget(null) },
    onDrop: (e: React.DragEvent) => { e.preventDefault(); setDragTarget(null); if (remaining > 0 && type) addFiles(e.dataTransfer.files, type) },
  })
  const retry = (item: Item) => { if (session && item.file) upload(item, session) }
  const dismiss = (key: string) => setItems(prev => prev.filter(i => i.key !== key))

  /** Take back a file that was not sent yet */
  const removeStored = async (item: Item) => {
    if (!session || !item.serverId) return
    try {
      const summary = await uploadLinkService.removeFile(session, item.serverId)
      setItems(prev => prev.filter(i => i.key !== item.key))
      setMissing((summary.missingTypes ?? []).filter(isDocumentType))
    } catch {
      // rare (already sent by the automatic send, or the connection dropped): say so, keep the file listed
      window.alert(t('uploadLink.removeFailed'))
    }
  }

  /** "Einsenden": everything waiting goes to the practice, one PDF per document type */
  const submit = async () => {
    if (!session) return
    setSubmitting(true)
    setSubmitResult(null)
    try {
      const r = await uploadLinkService.submit(session, { stampSignatureConfirmed: needsStampConfirm ? stampConfirmed : undefined })
      const sentIds = new Set(r.link.files.filter(f => !f.pending).map(f => f.id))
      setItems(prev => prev.map(i => (i.serverId && sentIds.has(i.serverId) ? { ...i, pending: false } : i)))
      setMissing((r.link.missingTypes ?? []).filter(isDocumentType))
      setLastSubmittedAt(r.submittedAt)
      setSubmitResult({ ok: r.failedTypes.length === 0, failed: r.failedTypes })
      // everything went out: the link is closed now
      if (r.failedTypes.length === 0) setPhase('done')
    } catch (err) {
      const code = getApiErrorCode(err)
      if (code === 'upload_session_expired' || code === 'upload_session_required') { window.location.reload(); return }
      if (code === 'upload_link_expired') { setPhase('expired'); return }
      if (code === 'upload_link_revoked') { setPhase('revoked'); return }
      if (code === 'upload_link_submitted') { setPhase('done'); return }
      setSubmitResult({ ok: false })
    } finally {
      setSubmitting(false)
    }
  }

  const busy = items.some(i => i.status === 'uploading' || i.status === 'queued')
  const pendingCount = items.filter(i => i.status === 'done' && i.pending).length
  const stampTypes = [...new Set(items.filter(i => i.status === 'done' && i.pending && STAMP_TYPES.has(i.documentType ?? '')).map(i => i.documentType as string))]
  const needsStampConfirm = stampTypes.length > 0
  const full = remaining <= 0
  const lang = i18n.language
  const formatDate = (iso: string) => new Date(iso).toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' })
  const formatTime = (iso: string) => new Date(iso).toLocaleString(lang, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  // In lists the short name is enough — the examples in brackets are for choosing
  const typeLabel = (type?: string | null) => (isDocumentType(type) ? t(`documentTypes.${type}`).replace(/\s*\([^)]*\)\s*$/, '') : null)
  const requestedMode = requested.length > 0
  // A file belongs to a requested area only if it was sent since the request (a document asked for
  // again must not look received because of an older one); files of this visit always count
  const forRequest = (i: Item) => !requestedAt || !i.at || i.key.startsWith('c-') || new Date(i.at).getTime() >= new Date(requestedAt).getTime()
  const extraItems = items.filter(i => !(requested.includes(i.documentType ?? '') && forRequest(i)))

  /** One uploaded (or uploading, or refused) file. */
  const fileRow = (item: Item, withType: boolean) => (
    <li key={item.key} className="px-3 py-2.5 flex items-start gap-3 bg-white" data-status={item.status}>
      <span className="mt-0.5 flex-shrink-0">
        {item.status === 'done' && (item.pending ? <Clock className="w-5 h-5 text-amber-600" /> : <CheckCircle2 className="w-5 h-5 text-green-600" />)}
        {(item.status === 'uploading' || item.status === 'queued') && <Loader2 className="w-5 h-5 text-primary animate-spin" />}
        {item.status === 'error' && <AlertTriangle className="w-5 h-5 text-destructive" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-body text-foreground truncate" title={item.name}>{item.name}</p>
        {item.status === 'done' && (
          <p className="text-small text-muted-foreground">
            {[withType ? typeLabel(item.documentType) : null, formatFileSize(item.size, lang), item.at ? formatTime(item.at) : null].filter(Boolean).join(' · ')}
            {' · '}<span className={item.pending ? 'text-amber-700' : 'text-green-700'}>{item.pending ? t('uploadLink.pendingFile') : t('uploadLink.sentFile')}</span>
          </p>
        )}
        {(item.status === 'uploading' || item.status === 'queued') && (
          <>
            <p className="text-small text-muted-foreground">{t('uploadLink.uploading')} {item.status === 'uploading' ? `${item.progress}%` : ''}</p>
            <div className="mt-1 h-1.5 rounded bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${item.progress}%` }} /></div>
          </>
        )}
        {item.status === 'error' && item.error && link && (
          <p className="text-small text-destructive">
            {t(`uploadLink.error.${item.error}`, { size: formatFileSize(item.size, lang), max: formatFileSize(link.maxBytes || MAX_UPLOAD_BYTES, lang) })}
          </p>
        )}
      </div>
      {item.status === 'done' && item.pending && item.serverId && (
        <button type="button" onClick={() => void removeStored(item)} className="p-1.5 rounded hover:bg-muted text-muted-foreground flex-shrink-0" title={t('uploadLink.removeFile')} aria-label={t('uploadLink.removeFile')}>
          <Trash2 className="w-4 h-4" />
        </button>
      )}
      {item.status === 'error' && (
        <span className="flex items-center gap-1 flex-shrink-0">
          {item.file && !full && (
            <button type="button" onClick={() => retry(item)} className="p-1.5 rounded hover:bg-primary/10 text-primary" title={t('uploadLink.retry')} aria-label={t('uploadLink.retry')}>
              <RotateCw className="w-4 h-4" />
            </button>
          )}
          <button type="button" onClick={() => dismiss(item.key)} className="p-1.5 rounded hover:bg-muted text-muted-foreground" title={t('uploadLink.remove')} aria-label={t('uploadLink.remove')}>
            <X className="w-4 h-4" />
          </button>
        </span>
      )}
    </li>
  )

  /** File and camera buttons for one upload area. */
  const pickButtons = (type: string | null, primaryLabel: string, secondary = false) => (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
      <Button type="button" variant={secondary ? 'secondary' : undefined} disabled={!type} onClick={() => type && pick(type)} className="w-full sm:w-auto inline-flex items-center justify-center gap-2">
        {secondary ? <Plus className="w-4 h-4" /> : <FileText className="w-4 h-4" />} {primaryLabel}
      </Button>
      {isMobile && (
        <Button type="button" variant="secondary" disabled={!type} onClick={() => type && pick(type, true)} className="w-full sm:w-auto inline-flex items-center justify-center gap-2">
          <Camera className="w-4 h-4" /> {t('uploadLink.takePhoto')}
        </Button>
      )}
    </div>
  )

  /** The patient says what the document is, then uploads (general link, or "another document"). */
  const generalUploader = (
    <>
      <label className="block mb-3">
        <span className="block text-body font-medium text-foreground mb-1.5">{t('uploadLink.documentTypeLabel')}</span>
        <select
          value={docType}
          onChange={e => setDocType(e.target.value)}
          className={cn('w-full px-3 py-2.5 border rounded-input text-body bg-white', docType ? 'border-border text-foreground' : 'border-primary text-muted-foreground')}
          data-testid="upload-document-type"
        >
          {!docType && <option value="" disabled>{t('uploadLink.documentTypePlaceholder')}</option>}
          {CHOOSABLE_DOCUMENT_TYPES.map(type => <option key={type} value={type} className="text-foreground">{t(`documentTypes.${type}`)}</option>)}
        </select>
      </label>
      <div
        {...dropProps('general', docType || null)}
        className={cn('mb-2 rounded-card border-2 border-dashed px-4 py-8 text-center transition-colors', dragTarget === 'general' ? 'border-primary bg-primary/5' : 'border-border bg-muted/30')}
      >
        <UploadCloud className="w-10 h-10 text-primary mx-auto mb-3" />
        {!isMobile && (
          <p className="text-body text-foreground mb-3">
            {t('uploadLink.dropTitle')} <span className="text-muted-foreground">{t('uploadLink.dropOr')}</span>
          </p>
        )}
        {pickButtons(docType || null, t('uploadLink.chooseFile'))}
        {!docType && <p className="text-small text-muted-foreground mt-3">{t('uploadLink.documentTypeFirst')}</p>}
        {STAMP_TYPES.has(docType) && <p className="text-small text-amber-900 mt-3 flex items-start gap-2 text-left"><Stamp className="w-4 h-4 flex-shrink-0 mt-0.5" /> {t('uploadLink.stampSignatureHint')}</p>}
      </div>
    </>
  )

  return (
    <div className="min-h-screen bg-muted flex flex-col">
      <main className="flex-1 flex items-start justify-center p-4 py-8">
        <div className="bg-white rounded-card shadow-sm p-6 sm:p-8 max-w-xl w-full">
          <div className="flex justify-center mb-6"><Logo size={100} /></div>

          {phase === 'loading' && (
            <div className="text-center py-8 text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> {t('common.loading')}
            </div>
          )}

          {(phase === 'invalid' || phase === 'expired' || phase === 'revoked') && (
            <div className="text-center py-4">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <h1 className="text-h2 text-foreground mb-2">{t(`uploadLink.${phase}.title`)}</h1>
              <p className="text-body text-muted-foreground">{t(`uploadLink.${phase}.body`)}</p>
              {tenant?.contactEmail && (
                <p className="text-small text-muted-foreground mt-4">
                  {t('uploadLink.contact')} <a className="underline" href={`mailto:${tenant.contactEmail}`}>{tenant.contactEmail}</a>
                </p>
              )}
            </div>
          )}

          {phase === 'done' && (
            <div className="text-center py-4" data-testid="upload-done">
              <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
              <h1 className="text-h2 text-foreground mb-2">{t('uploadLink.done.title')}</h1>
              <p className="text-body text-muted-foreground">{t('uploadLink.done.body')}</p>
              {tenant?.contactEmail && (
                <p className="text-small text-muted-foreground mt-4">
                  {t('uploadLink.contact')} <a className="underline" href={`mailto:${tenant.contactEmail}`}>{tenant.contactEmail}</a>
                </p>
              )}
            </div>
          )}

          {phase === 'ready' && link && (
            <>
              <h1 className="text-h2 text-foreground mb-3 text-center">{t('uploadLink.title')}</h1>
              <p className="text-body text-muted-foreground mb-1 text-center">
                {link.firstName ? t('uploadLink.greeting', { name: link.firstName }) : t('uploadLink.greetingNoName')} {t('uploadLink.intro')}
              </p>
              <p className="text-small text-muted-foreground mb-5 text-center">
                {t('uploadLink.patientNumber')}: <span className="font-medium text-foreground">{link.patientNumber}</span>
              </p>

              {link.message && (
                <div className="mb-5 rounded-card bg-primary/5 border border-primary/15 px-4 py-3 flex items-start gap-3">
                  <MessageSquare className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-small font-medium text-foreground">{t('uploadLink.staffNote')}</p>
                    <p className="text-body text-foreground whitespace-pre-line">{link.message}</p>
                  </div>
                </div>
              )}

              {full && (
                <div className="mb-5 rounded-card border border-amber-300 bg-amber-50 text-amber-900 text-small px-4 py-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{t('uploadLink.limitReached')}</span>
                </div>
              )}

              {requestedMode ? (
                <>
                  <h2 className="text-body font-medium text-foreground mb-3">{t('uploadLink.requestedTitle')}</h2>
                  <div className="space-y-3 mb-4">
                    {requested.map(type => {
                      const done = !missing.includes(type)
                      const own = items.filter(i => i.documentType === type && forRequest(i))
                      // received by the practice, or only uploaded and waiting for "Einsenden"
                      const sent = own.some(i => i.status === 'done' && !i.pending)
                      return (
                        <section
                          key={type}
                          data-testid={`upload-slot-${type}`}
                          data-done={done}
                          {...(!full && !isMobile ? dropProps(type, type) : {})}
                          className={cn(
                            'rounded-card border-2 p-4 transition-colors',
                            dragTarget === type ? 'border-primary bg-primary/5' : done ? 'border-green-200 bg-green-50/40' : 'border-dashed border-border bg-muted/20'
                          )}
                        >
                          <div className="flex items-start gap-2.5 mb-3">
                            {done ? <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> : <Circle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />}
                            <h3 className="flex-1 min-w-0 text-body font-medium text-foreground">{t(`documentTypes.${type}`)}</h3>
                            <span className={cn('text-small whitespace-nowrap mt-0.5', done ? 'text-green-700' : 'text-muted-foreground')}>
                              {!done ? t('uploadLink.requestedMissing') : sent ? t('uploadLink.requestedSent') : t('uploadLink.requestedReady')}
                            </span>
                          </div>
                          {STAMP_TYPES.has(type) && (
                            <p className="text-small text-amber-900 bg-amber-50 border border-amber-200 rounded-input px-3 py-2 mb-3 flex items-start gap-2" data-testid={`stamp-hint-${type}`}>
                              <Stamp className="w-4 h-4 flex-shrink-0 mt-0.5" /> {t('uploadLink.stampSignatureHint')}
                            </p>
                          )}
                          {own.length > 0 && (
                            <ul className="mb-3 divide-y divide-border border border-border rounded-card overflow-hidden" aria-live="polite">
                              {own.map(item => fileRow(item, false))}
                            </ul>
                          )}
                          {!full && pickButtons(type, done ? t('uploadLink.addAnother') : t('uploadLink.chooseFile'), done)}
                        </section>
                      )
                    })}
                  </div>
                  {missing.length === 0 && pendingCount > 0 && <p className="text-small text-foreground mb-4" data-testid="upload-all-ready">{t('uploadLink.allReady')}</p>}

                  {extraItems.length > 0 && (
                    <>
                      <h2 className="text-body font-medium text-foreground mb-2">{t('uploadLink.extraTitle')}</h2>
                      <ul className="mb-4 divide-y divide-border border border-border rounded-card overflow-hidden" aria-live="polite">
                        {extraItems.map(item => fileRow(item, true))}
                      </ul>
                    </>
                  )}

                  {!full && (
                    showExtra ? (
                      <div className="mb-2" data-testid="upload-extra">{generalUploader}</div>
                    ) : (
                      <button type="button" onClick={() => setShowExtra(true)} className="mb-4 inline-flex items-center gap-1.5 text-small text-primary underline-offset-2 hover:underline" aria-expanded={false} data-testid="upload-extra-toggle">
                        <Plus className="w-4 h-4" /> {t('uploadLink.otherDocument')}
                      </button>
                    )
                  )}
                </>
              ) : (
                <>
                  {!full && generalUploader}
                  <h2 className="text-body font-medium text-foreground mb-2 mt-6">{t('uploadLink.uploadedTitle')}</h2>
                  {items.length === 0 ? (
                    <p className="text-small text-muted-foreground mb-5">{t('uploadLink.uploadedNone')}</p>
                  ) : (
                    <ul className="mb-5 divide-y divide-border border border-border rounded-card overflow-hidden" aria-live="polite">
                      {items.map(item => fileRow(item, true))}
                    </ul>
                  )}
                </>
              )}

              {/* shared pickers; pick() remembers which upload area opened them */}
              <input ref={fileInput} type="file" accept={ACCEPT} multiple onChange={onPick} className="hidden" data-testid="upload-input" />
              <input ref={cameraInput} type="file" accept="image/jpeg,image/png" capture="environment" onChange={onPick} className="hidden" />

              {!full && <p className="text-small text-muted-foreground text-center mb-4">{t('uploadLink.formats', { max: formatFileSize(link.maxBytes || MAX_UPLOAD_BYTES, lang) })}</p>}
              {/* Einsenden: the practice receives the files only now (one PDF per document type) */}
              {(pendingCount > 0 || submitResult) && (
                <div className="mb-5 rounded-card border-2 border-primary/30 bg-primary/5 p-4" data-testid="upload-submit">
                  {submitResult?.ok && pendingCount === 0 ? (
                    <p className="text-body text-green-800 flex items-start gap-2" role="status"><CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" /> {t('uploadLink.submitted')}</p>
                  ) : (
                    <>
                      <p className="text-small text-foreground mb-3">{t('uploadLink.submitHint')}</p>
                      {requestedMode && missing.length > 0 && (
                        <p className="text-small text-amber-800 mb-3 flex items-start gap-2"><AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {t('uploadLink.submitMissing', { types: missing.map(m => typeLabel(m)).filter(Boolean).join(', ') })}</p>
                      )}
                      {submitResult && !submitResult.ok && (
                        <p className="text-small text-destructive mb-3" role="alert">
                          {submitResult.failed?.length ? t('uploadLink.submitPartial', { types: submitResult.failed.map(f => typeLabel(f)).filter(Boolean).join(', ') }) : t('uploadLink.submitFailed')}
                        </p>
                      )}
                      {needsStampConfirm && (
                        <label className="flex items-start gap-2.5 mb-3 text-small text-foreground cursor-pointer" data-testid="stamp-confirm">
                          <input type="checkbox" checked={stampConfirmed} onChange={e => setStampConfirmed(e.target.checked)} className="mt-0.5 w-4 h-4 flex-shrink-0 accent-primary" />
                          <span>{t('uploadLink.stampSignatureConfirm', { types: stampTypes.map(s => typeLabel(s)).filter(Boolean).join(', ') })}</span>
                        </label>
                      )}
                      <Button type="button" onClick={() => void submit()} disabled={submitting || busy || pendingCount === 0 || (requestedMode && missing.length > 0) || (needsStampConfirm && !stampConfirmed)} className="w-full inline-flex items-center justify-center gap-2">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {submitting ? t('common.sending') : t('uploadLink.submit', { count: pendingCount })}
                      </Button>
                    </>
                  )}
                </div>
              )}
              {lastSubmittedAt && pendingCount === 0 && !submitResult && (
                <p className="text-small text-green-700 mb-4 flex items-start gap-2"><CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /> {t('uploadLink.lastSubmitted', { date: formatTime(lastSubmittedAt) })}</p>
              )}

              <div className="space-y-1.5 text-small text-muted-foreground border-t border-border pt-4">
                {!full && <p>{t('uploadLink.remaining', { count: remaining })}</p>}
                <p>{t('uploadLink.validUntil', { date: formatDate(link.expiresAt) })}</p>
                <p className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{t('uploadLink.privacy')}</span></p>
                {tenant?.contactEmail && (
                  <p>{t('uploadLink.contact')} <a className="underline" href={`mailto:${tenant.contactEmail}`}>{tenant.contactEmail}</a></p>
                )}
              </div>
            </>
          )}
        </div>
      </main>
      <LegalFooter />
    </div>
  )
}
